import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

const ConfirmModal = ({ show, title, message, onConfirm, onCancel }) => {
  if (!show) return null;
  return (
    <>
      <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050 }}
        onClick={onCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          zIndex: 1055,
          width: "100%",
          maxWidth: 420,
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          padding: "28px 28px 22px",
        }}
      >
        <div className="d-flex align-items-center gap-3 mb-3">
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#fff0f0",
            }}
          >
            <i className="bi bi-trash3" style={{ fontSize: 20, color: "#dc3545" }} />
          </div>
          <h6 className="mb-0 fw-bold" style={{ fontSize: 16 }}>
            {title}
          </h6>
        </div>
        <p className="text-muted mb-4" style={{ fontSize: 14, lineHeight: 1.6 }}>
          {message}
        </p>
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-sm btn-danger px-4" style={{ borderRadius: 8 }} onClick={onConfirm}>
            Delete
          </button>
          <button className="btn btn-sm btn-outline-secondary px-4" style={{ borderRadius: 8 }} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

export default function AllVendors() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [modal, setModal] = useState({ show: false, vendor: null });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) {
      toast.error("You must be logged in.");
      navigate("/");
      return;
    }
  }, [navigate, token]);

  useEffect(() => {
    const fetchVendors = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/vendors`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch vendors.");
        const data = await res.json();
        setVendors(Array.isArray(data) ? data : data.content || []);
      } catch (err) {
        setError(err.message || "Failed to fetch vendors.");
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchVendors();
  }, [token]);

  const filteredVendors = useMemo(() => {
    const query = search.trim().toLowerCase();
    const list = vendors.filter((vendor) => {
      if (!query) return true;
      return [
        vendor.name,
        vendor.contactPerson,
        vendor.phone,
        vendor.email,
        vendor.gstNumber,
        vendor.address,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query));
    });

    if (!sortConfig.key) return list;

    return [...list].sort((a, b) => {
      const valueA = a[sortConfig.key] ?? "";
      const valueB = b[sortConfig.key] ?? "";
      if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [vendors, search, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortArrow = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const downloadReport = async () => {
    try {
      setDownloading(true);
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      const res = await fetch(`${API_BASE_URL}/api/vendors/report?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to download report");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = search ? `Vendors_Filtered_${date}.xlsx` : `Vendors_${date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded successfully");
    } catch (err) {
      toast.error(err.message || "Failed to download");
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    const vendor = modal.vendor;
    setModal({ show: false, vendor: null });
    if (!vendor) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/vendors/${vendor.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Unable to delete vendor.");
      setVendors((prev) => prev.filter((item) => item.id !== vendor.id));
      toast.success("Vendor deleted successfully.");
    } catch (err) {
      toast.error("Failed to delete vendor.");
    }
  };

  return (
    <>
      <div className="fixed-top">
        <AssetModuleNavbar />
      </div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="fw-bold mb-0">
              <i className="bi bi-buildings me-2"></i> Vendor Master
            </h4>
            <div className="text-muted mt-1" style={{ fontSize: 13 }}>
              Manage procurement, warranty, and service vendors in a dedicated master list.
            </div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={() => navigate("/addvendor")}>
              <i className="bi bi-plus-circle me-1"></i> New Vendor
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/allassets")}>
              <i className="bi bi-box-seam me-1"></i> Asset List
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={downloadReport} disabled={downloading}>
              <i className="bi bi-download me-1"></i>{downloading ? "Downloading..." : "Export Excel"}
            </button>
          </div>
        </div>

        <div className="row g-2 mb-3">
          {[
            { label: "Total Vendors", value: filteredVendors.length, icon: "bi-buildings", color: "#1e3a5f" },
            { label: "With GST", value: filteredVendors.filter((vendor) => vendor.gstNumber).length, icon: "bi-receipt", color: "#0d6efd" },
            { label: "With Email", value: filteredVendors.filter((vendor) => vendor.email).length, icon: "bi-envelope", color: "#198754" },
            { label: "With Phone", value: filteredVendors.filter((vendor) => vendor.phone).length, icon: "bi-telephone", color: "#fd7e14" },
          ].map((item) => (
            <div className="col-md-3 col-6" key={item.label}>
              <div className="bg-white border rounded p-2 d-flex align-items-center gap-2">
                <i className={`bi ${item.icon}`} style={{ color: item.color, fontSize: 20 }}></i>
                <div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>{item.label}</div>
                  <div className="fw-bold" style={{ fontSize: 18, color: item.color }}>
                    {item.value}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-2 bg-light border mb-2" style={{ cursor: "pointer" }} onClick={() => setShowFilters((prev) => !prev)}>
          <strong>{showFilters ? "− Filters" : "+ Filters"}</strong>
        </div>

        {showFilters && (
          <div className="row mb-3 g-2">
            <div className="col-md-3">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by vendor, contact, email, phone, GST..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <button className="btn btn-sm btn-outline-secondary w-40" onClick={() => setSearch("")}>
                Clear
              </button>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center p-2 bg-light border mb-2">
          <span className="text-muted" style={{ fontSize: 13 }}>
            {filteredVendors.length} vendor{filteredVendors.length !== 1 ? "s" : ""}
          </span>
          <Link to="/addvendor" className="text-decoration-none" style={{ fontSize: 13 }}>
            Quick add vendor
          </Link>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="table table-sm align-middle text-start">
            <thead className="table-light">
              <tr>
                <th style={{ width: 100 }}>View | Edit</th>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("name")}>
                  Vendor Name {getSortArrow("name")}
                </th>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("contactPerson")}>
                  Contact Person {getSortArrow("contactPerson")}
                </th>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("phone")}>
                  Phone {getSortArrow("phone")}
                </th>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("email")}>
                  Email {getSortArrow("email")}
                </th>
                <th>GST Number</th>
                <th>Address</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="text-muted mt-2" style={{ fontSize: 13 }}>Loading vendors...</div>
                  </td>
                </tr>
              ) : filteredVendors.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-5">
                    <i className="bi bi-buildings" style={{ fontSize: 32, opacity: 0.3 }}></i>
                    <div className="mt-2">
                      No vendors found. <Link to="/addvendor">Add your first vendor</Link>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredVendors.map((vendor) => (
                  <tr key={vendor.id}>
                    <td>
                      <Link to={`/viewvendor/${vendor.id}`} className="me-1 text-primary" style={{ fontSize: 13 }}>
                        View
                      </Link>
                      |
                      <Link to={`/editvendor/${vendor.id}`} className="ms-1 text-primary" style={{ fontSize: 13 }}>
                        Edit
                      </Link>
                    </td>
                    <td className="fw-semibold">{vendor.name || "—"}</td>
                    <td>{vendor.contactPerson || "—"}</td>
                    <td>{vendor.phone || "—"}</td>
                    <td>{vendor.email || "—"}</td>
                    <td>
                      {vendor.gstNumber ? (
                        <span
                          className="badge"
                          style={{ background: "#e8f0f8", color: "#1e3a5f", border: "1px solid #c8d8e8", fontFamily: "monospace" }}
                        >
                          {vendor.gstNumber}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={{ maxWidth: 240, fontSize: 13 }}>
                      {vendor.address ? (
                        <span title={vendor.address}>
                          {vendor.address.length > 70 ? `${vendor.address.slice(0, 70)}...` : vendor.address}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        {/* <button className="btn btn-sm btn-outline-warning" onClick={() => navigate(`/editvendor/${vendor.id}`)} title="Edit">
                          <i className="bi bi-pencil"></i>
                        </button> */}
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setModal({ show: true, vendor })}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {error && <p className="text-danger mt-3 mb-0">{error}</p>}
      </div>

      <ConfirmModal
        show={modal.show}
        title="Delete Vendor"
        message={`Are you sure you want to delete "${modal.vendor?.name || "this vendor"}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setModal({ show: false, vendor: null })}
      />
    </>
  );
}
